import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Key, MapPin, Clock, Wifi, Phone, AlertTriangle } from 'lucide-react';
import { guestAPI } from '../../lib/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const GuestCheckIn = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceForm, setServiceForm] = useState({ type: 'cleaning', description: '', urgency: 'normal' });
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    guestAPI.getCheckIn(bookingId)
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load check-in details'))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const handleServiceRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await guestAPI.submitServiceRequest({ bookingId, ...serviceForm });
      toast.success('Service request submitted! Our team will assist you shortly.');
      setShowServiceForm(false);
      setServiceForm({ type: 'cleaning', description: '', urgency: 'normal' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!data) return <div className="empty-state"><h3>Check-in details not available</h3></div>;

  const { booking, checkInCode, checkInInstructions, propertyRules, localContent } = data;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/guest/bookings')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">Check-in Information</h1>
            <p className="page-subtitle">{booking?.property?.title}</p>
          </div>
        </div>
      </div>

      {/* Stay summary banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1E40AF, #7C3AED)',
        borderRadius: 14, padding: '24px 28px', marginBottom: 24, color: '#fff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>YOUR STAY</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{booking?.property?.title}</div>
            {booking?.property?.address && (
              <div style={{ fontSize: 14, opacity: 0.8, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={13} />{booking.property.address.city}, {booking.property.address.country}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 28 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>CHECK IN</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{booking?.checkIn ? format(new Date(booking.checkIn), 'MMM d') : '—'}</div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>{propertyRules?.checkInTime || '3:00 PM'}</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>CHECK OUT</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{booking?.checkOut ? format(new Date(booking.checkOut), 'MMM d') : '—'}</div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>{propertyRules?.checkOutTime || '11:00 AM'}</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>NIGHTS</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{booking?.nights}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Access Code */}
          {checkInCode && (
            <div className="card" style={{ border: '2px solid #3B82F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Key size={20} color="#3B82F6" />
                </div>
                <span style={{ fontWeight: 700, fontSize: 16 }}>Access Code</span>
              </div>
              <div style={{
                background: '#F0F9FF', border: '2px dashed #93C5FD', borderRadius: 12,
                padding: '20px 24px', textAlign: 'center'
              }}>
                <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '0.2em', color: '#1D4ED8', fontFamily: 'monospace' }}>
                  {checkInCode}
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }}>
                  Do not share this code with others
                </div>
              </div>
            </div>
          )}

          {/* Check-in Instructions */}
          {checkInInstructions && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MapPin size={20} color="#059669" />
                </div>
                <span style={{ fontWeight: 700, fontSize: 16 }}>Check-in Instructions</span>
              </div>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {checkInInstructions}
              </div>
            </div>
          )}

          {/* House Rules */}
          {propertyRules && (
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>🏠 House Rules</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Check-in', value: propertyRules.checkInTime || '3:00 PM', icon: '🕒' },
                  { label: 'Check-out', value: propertyRules.checkOutTime || '11:00 AM', icon: '🕛' },
                  { label: 'Smoking', value: propertyRules.smokingAllowed ? 'Allowed' : 'Not Allowed', icon: propertyRules.smokingAllowed ? '✅' : '❌' },
                  { label: 'Pets', value: propertyRules.petsAllowed ? 'Allowed' : 'Not Allowed', icon: propertyRules.petsAllowed ? '✅' : '❌' },
                  { label: 'Parties', value: propertyRules.partiesAllowed ? 'Allowed' : 'Not Allowed', icon: propertyRules.partiesAllowed ? '✅' : '❌' },
                ].map(({ label, value, icon }) => (
                  <div key={label} style={{ display: 'flex', gap: 8, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, fontSize: 13 }}>
                    <span>{icon}</span>
                    <span style={{ color: '#6B7280' }}>{label}:</span>
                    <span style={{ fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Local Content */}
          {localContent?.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📍 Local Recommendations</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {localContent.slice(0, 6).map(c => (
                  <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#F9FAFB', borderRadius: 8 }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.title}</div>
                      {c.description && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {c.priceRange && <span className="badge badge-gray">{c.priceRange}</span>}
                      {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary" onClick={e => e.stopPropagation()}>Visit</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Emergency Contact */}
          <div className="card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Phone size={16} color="#D97706" />
              <span style={{ fontWeight: 700, color: '#92400E' }}>Need Help?</span>
            </div>
            <p style={{ fontSize: 13, color: '#78350F', lineHeight: 1.5 }}>
              Our team is available 24/7. Use the service request button below for any issues during your stay.
            </p>
            <button
              className="btn btn-sm"
              style={{ marginTop: 12, background: '#D97706', color: '#fff', width: '100%', justifyContent: 'center' }}
              onClick={() => setShowServiceForm(true)}
            >
              <AlertTriangle size={14} /> Request Service
            </button>
          </div>

          {/* Quick Info */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Quick Info</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['Booking #', booking?.bookingNumber],
                ['Guests', `${(booking?.guests?.adults || 1) + (booking?.guests?.children || 0)}`],
                ['Status', booking?.status?.replace('_', ' ')],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#9CA3AF' }}>{k}</span>
                  <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Service Request Modal */}
      {showServiceForm && (
        <div className="modal-overlay" onClick={() => setShowServiceForm(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Request Service</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowServiceForm(false)}>✕</button>
            </div>
            <form onSubmit={handleServiceRequest}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Service Type</label>
                  <select className="form-select" value={serviceForm.type} onChange={e => setServiceForm(p => ({ ...p, type: e.target.value }))}>
                    <option value="cleaning">🧹 Housekeeping / Cleaning</option>
                    <option value="maintenance">🔧 Maintenance Issue</option>
                    <option value="supplies">🛒 Extra Supplies</option>
                    <option value="other">📋 Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Urgency</label>
                  <select className="form-select" value={serviceForm.urgency} onChange={e => setServiceForm(p => ({ ...p, urgency: e.target.value }))}>
                    <option value="normal">Normal — within a few hours</option>
                    <option value="urgent">Urgent — as soon as possible</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <textarea className="form-textarea" rows={3} placeholder="Describe what you need..." required
                    value={serviceForm.description} onChange={e => setServiceForm(p => ({ ...p, description: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowServiceForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestCheckIn;
